import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys, useAuthenticatedClient } from './queries'
import type { components } from './schema'

/** Safeware's plan catalog as synced into our system (USA-1161). */
export type SafewarePlanCatalog = components['schemas']['SafewarePlanCatalogRead']
export type SafewarePlan = components['schemas']['SafewarePlanRead']
export type SafewarePlanChange = components['schemas']['SafewarePlanChangeRead']

const safewarePlansKey = ['safeware', 'plans'] as const

export function useSafewarePlans(enabled = true) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: safewarePlansKey,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/safeware/plans', {})
      if (error) throw error
      return data
    },
    enabled,
    staleTime: 60_000,
  })
}

export function useSyncSafewarePlans() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/safeware/plans/sync', {})
      if (error) throw error
      return data
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: safewarePlansKey })
      queryClient.invalidateQueries({ queryKey: queryKeys.products.all })
    },
  })
}

/** Mark a plan change reviewed; it moves below the open ones at once. */
export function useReviewSafewarePlanChange() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (changeId: number) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/safeware/plan-changes/{change_id}/review', {
        params: { path: { change_id: changeId } },
      })
      if (error) throw error
      return data
    },
    onMutate: async (changeId) => {
      await queryClient.cancelQueries({ queryKey: safewarePlansKey })
      const previous = queryClient.getQueryData<SafewarePlanCatalog>(safewarePlansKey)
      if (previous) {
        queryClient.setQueryData<SafewarePlanCatalog>(safewarePlansKey, {
          ...previous,
          changes: previous.changes.map((change) =>
            change.id === changeId ? { ...change, reviewed_at: new Date().toISOString() } : change,
          ),
        })
      }
      return { previous }
    },
    onError: (_error, _changeId, context) => {
      if (context?.previous) queryClient.setQueryData(safewarePlansKey, context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: safewarePlansKey })
    },
  })
}

/** An order's equipment covered by Safeware plans, one entry per unit, with its claims (USA-1165). */
export type SafewareProtectedUnit = components['schemas']['SafewareProtectedUnitRead']
export type SafewareClaim = components['schemas']['SafewareClaimRead']
export type SafewareClaimDraft = components['schemas']['SafewareClaimDraftRead']
export type SafewareClaimCreate = components['schemas']['SafewareClaimCreate']

const protectedUnitsKey = (orderUuid: string) => ['safeware', 'orders', orderUuid, 'units'] as const

export function useOrderProtectedUnits(orderUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: protectedUnitsKey(orderUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/safeware/orders/{order_uuid}/units', {
        params: { path: { order_uuid: orderUuid } },
      })
      if (error) throw error
      return data
    },
  })
}

/** The claim form pre-filled from Safeware's coverage record (one Coverage Lookup per open). */
export function useSafewareClaimDraft(unitUuid: string | null) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: ['safeware', 'units', unitUuid, 'claim-draft'],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/safeware/units/{unit_uuid}/claim-draft', {
        params: { path: { unit_uuid: unitUuid ?? '' } },
      })
      if (error) throw error
      return data
    },
    enabled: unitUuid !== null,
    staleTime: 0,
    gcTime: 0,
  })
}

/** File a claim with Safeware; the claim and its first status join the unit at once. */
export function useFileSafewareClaim(orderUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ unitUuid, body }: { unitUuid: string; body: SafewareClaimCreate }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/safeware/units/{unit_uuid}/claims', {
        params: { path: { unit_uuid: unitUuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: (claim, { unitUuid }) => {
      queryClient.setQueryData<SafewareProtectedUnit[]>(protectedUnitsKey(orderUuid), (units) =>
        units?.map((unit) => (unit.uuid === unitUuid ? { ...unit, claims: [claim, ...unit.claims] } : unit)),
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: protectedUnitsKey(orderUuid) })
    },
  })
}
